import React, { useMemo } from 'react';
import { Box, ClickToCopy, FraudIcon, Inscription, ListItem } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { ListItemType } from '@/common/types';
import { TextWithPointer } from '@/components/elements/TextWithPointer';
import Link from '@/modules/core/components/Link';
import { Employee } from '@/modules/employee/employee.types';
import {
  EMPLOYEE_COLUMN_WIDTHS,
  FRAUD_COLUMN_WIDTH,
  STATUS_COLUMN_WIDTH,
} from '@/modules/employee/Employees/EmployeesList/EmployeesList.constants';
import theme from '@/styles/theme';

interface OrganizationNamesProps {
  employerName?: string,
  subsidiaryName?: string,
}

const ORGANIZATION_NAME_MAX_LENGTH = 20;

const truncateOrganizationName = (name: string) => (name.length > ORGANIZATION_NAME_MAX_LENGTH
  ? `${name.slice(0, ORGANIZATION_NAME_MAX_LENGTH)}…`
  : name);

const OrganizationNames: React.FC<OrganizationNamesProps> = ({ employerName, subsidiaryName }) => (
  <Box>
    {
      employerName && (
        <TextWithPointer
          title={employerName}
          disableHovering
          ellipsisMode
        >
          {truncateOrganizationName(employerName)}
        </TextWithPointer>
      )
    }
    {subsidiaryName && (
      <TextWithPointer
        title={subsidiaryName}
        margin={{ top: 'spacing2' }}
        weight={500}
        size="12px"
        color="textSecondary"
        disableHovering
        ellipsisMode
      >
        {truncateOrganizationName(subsidiaryName)}
      </TextWithPointer>
    )}
  </Box>
);

const fixedColumnStyle = (width: string): React.CSSProperties => ({
  flex: `0 0 ${width}`,
  marginRight: theme.spacings.spacing12,
});

const useEmployeesList = (data: Employee[], showFraudIndicator = false) => {
  const formattedList = useMemo<ListItem[]>(() => data?.map((item: Employee) => {
    const hasFraudIcon = showFraudIndicator && Boolean(item.isFraudReviewRequired);

    return {
    id: item.id,
    fields: [
      {
        key: 'lastName',
        type: ListItemType.Info,
        title: item.lastName || '',
        link: ROUTES.EMPLOYEE_VIEW(item.id),
        fieldStyle: fixedColumnStyle(EMPLOYEE_COLUMN_WIDTHS.lastName),
        ellipsisMode: true,
        node: (
          <Link to={ROUTES.EMPLOYEE_VIEW(item.id)}>
            <TextWithPointer
              title={item.lastName}
              size="medium"
              weight="bold"
              color="textBody"
              ellipsisMode
            >
              {item.lastName || ''}
            </TextWithPointer>
          </Link>
        ),
      },
      {
        key: 'firstName',
        type: ListItemType.Info,
        title: item.firstName || '',
        link: ROUTES.EMPLOYEE_VIEW(item.id),
        fieldStyle: fixedColumnStyle(EMPLOYEE_COLUMN_WIDTHS.firstName),
        node: (
          <Link to={ROUTES.EMPLOYEE_VIEW(item.id)}>
            <TextWithPointer
              title={item.firstName}
              size="medium"
              weight="bold"
              color="textBody"
              ellipsisMode
            >
              {item.firstName || ''}
            </TextWithPointer>
          </Link>
        ),
      },
      {
        key: 'ssn',
        type: ListItemType.Node,
        title: item.ssn || '',
        node: (
          <Box direction="row" align="center">
            <Inscription textAlign="center">{item.ssn}</Inscription>
          </Box>
        ),
        fieldStyle: fixedColumnStyle(EMPLOYEE_COLUMN_WIDTHS.ssn),
      },
      {
        key: 'employerEmployeeId',
        type: ListItemType.Node,
        title: item.employerEmployeeId,
        node: (
          <Box margin={{ right: 'spacing4' }}>
            <ClickToCopy
              title={item.employerEmployeeId || ''}
              testId={`employerEmployeeId_${item.id}`}
            />
          </Box>
        ),
        fieldStyle: fixedColumnStyle(EMPLOYEE_COLUMN_WIDTHS.employerEmployeeId),
        ellipsisMode: true,
      },
      {
        key: 'partnerEmployeeId',
        type: ListItemType.Node,
        title: item.partnerEmployeeId,
        node: (
          <Box margin={{ right: 'spacing4' }}>
            <ClickToCopy
              title={item.partnerEmployeeId}
              testId={`partnerEmployeeId_${item.id}`}
            />
          </Box>
        ),
        fieldStyle: fixedColumnStyle(EMPLOYEE_COLUMN_WIDTHS.partnerEmployeeId),
        ellipsisMode: true,
      },
      {
        key: 'employer',
        type: ListItemType.Node,
        title: item.employerName || '',
        node: (
          <OrganizationNames
            employerName={item.employerName}
            subsidiaryName={item.subsidiaryName}
          />
        ),
        fieldStyle: { flex: 1, marginRight: theme.spacings.spacing12 },
        ellipsisMode: true,
      },
      {
        key: 'status',
        type: ListItemType.Status,
        title: item.status || '',
        fieldStyle: {
          flex: hasFraudIcon
            ? `0 0 ${STATUS_COLUMN_WIDTH}`
            : `0 0 calc(${STATUS_COLUMN_WIDTH} + ${theme.spacings.spacing12} + ${FRAUD_COLUMN_WIDTH})`,
          alignItems: 'flex-end',
        },
        isReverse: true,
      },
      ...(hasFraudIcon
        ? [{
          key: 'fraudReview',
          type: ListItemType.Node,
          title: '',
          fieldStyle: {
            flex: `0 0 ${FRAUD_COLUMN_WIDTH}`,
            marginLeft: theme.spacings.spacing12,
            alignItems: 'center',
            justifyContent: 'center',
          },
          node: (
            <FraudIcon
              size="24px"
              color="textTitle"
              a11yTitle="Fraud review required"
              data-testid={`employees-list_item_${item.id}_fraud-icon`}
            />
          ),
        }]
        : []),
    ],
  };
  }),
  [data, showFraudIndicator]);

  return formattedList;
};

export default useEmployeesList;
